import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, Badge, Alert, Form, Button } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import DatePeriodNavigation, { PeriodChangeEvent } from './common/DatePeriodNavigation';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import { FilterBar } from './common/FilterControls';
import type { FilterDefinition } from './common/FilterControls';
import { ApiErrorBanner } from './common/ApiErrorBanner';
import InvoiceDetailModal from './InvoiceDetailModal';
import TransactionDetailModal from './TransactionDetailModal';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import {
  getCommissionPayments,
  getCommissionsUserIdPayments,
  getBankTransactionsTransactionId,
  patchCommissionPayments,
  postCommissionPaymentsSync,
  postCommissionPaymentsIdSync,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  User,
  CommissionPaymentRecord,
  GetCommissionPaymentsParams,
  GetCommissionsUserIdPaymentsParams,
  CommissionPaymentsSummary,
  BankTransaction,
} from '../api/generated/model';

const FETCH_LIMIT = 50;

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function formatLocalDate(s?: string): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

const sourceTypeOptions = [
  { value: 'imported_commission_files', label: 'Import' },
  { value: 'bank_transactions', label: 'Bank' },
  { value: 'mercury_transactions', label: 'Mercury' },
  { value: 'manual_commission_entries', label: 'Manual' },
];

const sourceTypeLabel = (st?: string): string => {
  if (!st) return '';
  const match = sourceTypeOptions.find(o => o.value === st);
  return match ? match.label : st;
};

// Tri-state checkbox used in the select column header: checked when all rows
// are selected, indeterminate (dash) when some are, empty when none are.
// Native <input> with a ref so we can set the `indeterminate` DOM property,
// which isn't expressible via React's checked prop.
const TriStateCheckbox: React.FC<{ state: 'none' | 'some' | 'all'; ariaLabel?: string }> = ({ state, ariaLabel }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === 'some';
    }
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={state === 'all'}
      readOnly
      tabIndex={-1}
      style={{ pointerEvents: 'none', margin: 0 }}
      aria-label={ariaLabel}
    />
  );
};

// Filter keys persisted to the URL. When scope='user' the user_id filter
// doesn't apply (pinned via path) and is omitted from the persisted set.
const adminFilterKeys = ['user_id', 'source_type', 'search'] as const;
const userFilterKeys = ['source_type', 'search'] as const;

export type BucketBy = 'booking_date' | 'paid_at';

export interface CommissionPaymentsTableProps {
  // 'admin' → fetches GET /commission-payments and shows User column + User filter.
  // 'user'  → fetches GET /commissions/{userId}/payments (no User column/filter).
  scope: 'admin' | 'user';

  // Required when scope='user'. Scopes the fetch and hides the User column.
  userId?: string;

  // Date range — always controlled by parent. Parent owns how this is sourced
  // (URL state for admin; parent month selection for per-user embedding).
  dateRange: { start_date: string; end_date: string };

  // When provided, the component renders a DatePeriodNavigation picker.
  // The admin tab uses this; per-user embed omits it (parent already picks).
  period?: string;
  onPeriodChange?: (e: PeriodChangeEvent) => void;

  // URL-sync gate for filters/sort. When location.pathname === urlPathname,
  // filter/sort changes persist to the URL. When undefined, URL sync is off.
  urlPathname?: string;

  // DataTable preferences.
  preferencesKey: string;
  currentUserId?: string;
  userPreferences?: any;

  // User list for the User filter dropdown (admin scope only).
  allUsers?: User[];

  // Issues mode: filters the result set to rows whose validation_errors is
  // non-null, and switches the validation column to render the full message
  // text inline (rather than just a tooltip-only ⚠ icon).
  validationErrorsOnly?: boolean;

  // When set, the bucket-by toggle is hidden and this value is used. Use this
  // to force the per-user-period drill-in to booking_date semantics, or to
  // force the Issues view to paid_at (so linkless-with-errors rows surface).
  lockBucketBy?: BucketBy;
}

export const CommissionPaymentsTable: React.FC<CommissionPaymentsTableProps> = ({
  scope,
  userId,
  dateRange,
  period,
  onPeriodChange,
  urlPathname,
  preferencesKey,
  currentUserId,
  userPreferences,
  validationErrorsOnly,
  allUsers,
  lockBucketBy,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Select + bulk delete are gated on role — only admin/finance can mutate
  // commission payments. Other roles (vp_sales, sales_manager, etc.) see
  // the table read-only, without the select column or bulk-delete button.
  // Revalidate is a separate, less-privileged action — it only recomputes
  // validation_errors against current invoice state, so VP / sales
  // managers can use it to refresh the Issues view after they (or
  // someone else) corrected attribution data in QuickBooks.
  const { user: authUser } = useSelector((state: RootState) => state.auth);
  const canDelete = authUser?.role === 'admin' || authUser?.role === 'finance';
  const canRevalidate = canDelete
    || authUser?.role === 'vp_sales'
    || authUser?.role === 'sales_manager';

  const filterKeys = scope === 'admin' ? adminFilterKeys : userFilterKeys;

  // Filters — initialized from URL when scope/pathname allow it.
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    if (!urlPathname) return {};
    const f: Record<string, any> = {};
    for (const key of filterKeys) {
      const val = searchParams.get(key);
      if (val) f[key] = val;
    }
    return f;
  });
  const [showFilterBar, setShowFilterBar] = useState(() => {
    if (!urlPathname) return false;
    return filterKeys.some(k => searchParams.has(k));
  });

  // Bucket-by toggle. When locked by parent, that value is fixed (per-period
  // drill-ins force booking_date so the table matches the period summary;
  // Issues view forces paid_at so linkless-with-errors rows surface).
  // Otherwise the all-payments view defaults to booking_date (sales' "what got
  // paid for the invoices booked this period" semantics) and the user can
  // toggle to paid_at for cash-flow / reconciliation.
  const [internalBucketBy, setInternalBucketBy] = useState<BucketBy>(() => {
    if (lockBucketBy) return lockBucketBy;
    if (!urlPathname) return 'booking_date';
    const v = searchParams.get('bucket_by');
    return v === 'paid_at' ? 'paid_at' : 'booking_date';
  });
  const bucketBy: BucketBy = lockBucketBy ?? internalBucketBy;

  // Sort default mirrors bucket — booking_date when bucketing on booking_date,
  // paid_at otherwise. URL state takes precedence when present.
  const defaultSortField: 'booking_date' | 'paid_at' = bucketBy === 'paid_at' ? 'paid_at' : 'booking_date';
  const [sortField, setSortField] = useState<string>(() => {
    if (!urlPathname) return defaultSortField;
    return searchParams.get('sort_field') || defaultSortField;
  });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    if (!urlPathname) return 'desc';
    return (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc';
  });

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v).length,
    [filters]
  );

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  // Row identity. Composite of (id, link_id) — the table renders one row per
  // (commission payment, link) tuple so payments with multiple links appear
  // as separate rows. Same fn drives the hook's cross-page duplicate
  // detection and DataTable's React keys.
  const getRowKey = useCallback(
    (r: CommissionPaymentRecord) => `${r.id || ''}:${r.link_id || 'none'}`,
    []
  );

  // Cursor-based query-param builder. First page sends filters/sort; subsequent
  // pages send only the cursor (the cursor encodes the rest).
  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) {
        return { cursor, limit: FETCH_LIMIT };
      }
      const base: Record<string, any> = {
        sort_field: sortField,
        sort_direction: sortDir,
        bucket_by: bucketBy,
        limit: FETCH_LIMIT,
      };
      // Issues view scopes by validation flag, not date — we want every open
      // issue regardless of when it was paid. All other modes filter by date.
      // Skip empty values so we never serialize start_date= or end_date=
      // with no value — the spec types these as `format: date` and the
      // OpenAPI binder rejects empty strings with a 400 before the
      // handler runs.
      if (!validationErrorsOnly) {
        if (dateRange.start_date) base.start_date = dateRange.start_date;
        if (dateRange.end_date) base.end_date = dateRange.end_date;
      }
      if (scope === 'admin' && filters.user_id) base.user_id = filters.user_id;
      if (filters.source_type) base.source_type = filters.source_type;
      if (filters.search) base.search = filters.search;
      if (validationErrorsOnly) base.has_validation_errors = true;
      return base;
    },
    [dateRange, filters, sortField, sortDir, bucketBy, scope, validationErrorsOnly]
  );

  const {
    allItems: allPayments,
    totalCount,
    apiSummary,
    isLoading,
    hasMore,
    handleLoadMore,
    setAllItems: setAllPayments,
    reset: resetPagination,
    error: paginationError,
  } = usePaginatedQuery<CommissionPaymentRecord, any, CommissionPaymentsSummary>({
    // Include userId in the query key when scope='user' so admin and per-user
    // instances don't share cache entries.
    queryKey: scope === 'user' && userId ? `commission-payments:${userId}` : 'commission-payments',
    buildQueryParams,
    queryFn: (params) => {
      if (scope === 'user') {
        return getCommissionsUserIdPayments(userId!, params as GetCommissionsUserIdPaymentsParams);
      }
      return getCommissionPayments(params as GetCommissionPaymentsParams);
    },
    getRowKey,
  });

  // Selection + bulk delete.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const allPaymentsRef = useRef(allPayments);
  allPaymentsRef.current = allPayments;

  const hasSelection = selectedIds.size > 0;

  const toggleRow = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    const ids = selectedIdsRef.current;
    if (ids.size > 0) {
      setSelectedIds(new Set());
      return;
    }
    const payments = allPaymentsRef.current;
    setSelectedIds(() => {
      const next = new Set<string>();
      for (const p of payments) if (p.id) next.add(p.id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Reset selection when the underlying page-1 params change.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [dateRange.start_date, dateRange.end_date, filters, bucketBy]);

  // Toggling bucket changes the row semantics (per-link vs per-cp+linkless),
  // so reset sort to the bucket's natural default and clear the cached pages.
  const handleBucketChange = useCallback((next: BucketBy) => {
    if (next === bucketBy) return;
    setInternalBucketBy(next);
    setSortField(next === 'paid_at' ? 'paid_at' : 'booking_date');
    setSortDir('desc');
    setAllPayments([]);
    clearSelection();
  }, [bucketBy, setAllPayments, clearSelection]);

  // Revalidate state — set of row IDs currently being revalidated so each
  // button can show its own spinner without disabling the rest of the table.
  // bulkRevalidating is a separate flag for the header "Re-validate all"
  // action, which doesn't have a row id to key off. Mirrored to refs so
  // the columns useMemo (intentionally cached on []-ish deps to avoid
  // resetting DataTable's column manager) can read the live state.
  const [revalidatingIds, setRevalidatingIds] = useState<Set<string>>(new Set());
  const [bulkRevalidating, setBulkRevalidating] = useState(false);
  const revalidatingIdsRef = useRef(revalidatingIds);
  revalidatingIdsRef.current = revalidatingIds;
  const bulkRevalidatingRef = useRef(bulkRevalidating);
  bulkRevalidatingRef.current = bulkRevalidating;
  const [revalidateBanner, setRevalidateBanner] = useState<{
    variant: 'success' | 'warning' | 'danger';
    message: string;
  } | null>(null);

  const commissionsQueryKey = scope === 'user' && userId
    ? `commission-payments:${userId}`
    : 'commission-payments';

  const handleRevalidateRow = useCallback(async (id: string) => {
    setRevalidatingIds(prev => new Set(prev).add(id));
    try {
      const r = await postCommissionPaymentsIdSync(id);
      const checked = r.checked || 0;
      const cleared = r.cleared || 0;
      const stillErroring = r.still_erroring || 0;
      const messages = r.messages || [];
      // The right success signal is "no link still in error", not
      // "this revalidate cleared one". The refetch step (QB invoice
      // and/or Mercury BT) often re-derives the CP and writes a clean
      // value before RevalidateLinks reads it — that shows up as
      // no_change rather than cleared, but the issue is gone all the
      // same.
      let main: string;
      let variant: 'success' | 'warning' | 'danger';
      if (checked === 0) {
        main = 'No link to validate — the underlying record may have been replaced. Refresh to see current state.';
        variant = 'warning';
      } else if (stillErroring === 0) {
        main = cleared > 0
          ? 'Cleared the validation error on this payment.'
          : 'No validation error on this payment.';
        variant = 'success';
      } else {
        main = 'Issue still present after revalidation — fix the linked invoice or bank-transaction note.';
        variant = 'warning';
      }
      setRevalidateBanner({
        variant,
        message: messages.length > 0 ? `${main} ${messages.join(' · ')}` : main,
      });
      // Reset the hook's local accumulator state and trigger a fresh
      // first-page fetch. removeQueries clears the cache but doesn't
      // reliably re-trigger fetch on active subscribers; invalidate
      // marks stale + refetches active. resetPagination wipes allItems
      // pre-emptively so the brief render between "stale cache" and
      // "refetch landed" doesn't paint old strings into rows that are
      // about to be replaced.
      resetPagination();
      queryClient.invalidateQueries({ queryKey: [commissionsQueryKey] });
      // Also refresh the Orval-generated count queries that drive the
      // issues-tab badge. Their keys are URL-style prefixes that don't
      // match the accumulator key above, so they stay stale otherwise.
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commission-payments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commissions'] });
      // Stranded BTs query is keyed at a deeper path that is element-
      // by-element distinct from '/api/v1/commissions', so the prefix
      // match above doesn't catch it. Operators expect "Re-validate
      // all issues" to refresh every section of the Issues view, so
      // explicitly invalidate the stranded query too.
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commissions/stranded-bank-transactions'] });
    } catch (err: any) {
      setRevalidateBanner({
        variant: 'danger',
        message: `Re-validate failed: ${err?.response?.data?.message || err?.message || err}`,
      });
    } finally {
      setRevalidatingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [commissionsQueryKey, queryClient, resetPagination]);

  const handleRevalidateAll = useCallback(async () => {
    setBulkRevalidating(true);
    try {
      const r = await postCommissionPaymentsSync();
      const checked = r.checked || 0;
      const cleared = r.cleared || 0;
      const stillErroring = r.still_erroring || 0;
      setRevalidateBanner({
        variant: stillErroring > 0 ? 'warning' : 'success',
        message: checked === 0
          ? `No commission payments to scan.`
          : stillErroring === 0
            ? `Scanned ${checked} payment link${checked === 1 ? '' : 's'} — all clean.`
            : `Scanned ${checked} payment link${checked === 1 ? '' : 's'}: ${stillErroring} flagged${cleared > 0 ? `, ${cleared} cleared` : ''}.`,
      });
      // See handleRevalidateRow — reset the local accumulator state
      // then invalidate so the active query refetches and the new page
      // replaces wiped allItems cleanly. Also refresh the issues-tab-
      // badge count queries (different URL-style keys).
      resetPagination();
      queryClient.invalidateQueries({ queryKey: [commissionsQueryKey] });
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commission-payments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commissions'] });
      // See handleRevalidateRow — element-by-element prefix match means
      // ['/api/v1/commissions'] does NOT cover the stranded-BT query.
      queryClient.invalidateQueries({ queryKey: ['/api/v1/commissions/stranded-bank-transactions'] });
    } catch (err: any) {
      setRevalidateBanner({
        variant: 'danger',
        message: `Re-validate all failed: ${err?.response?.data?.message || err?.message || err}`,
      });
    } finally {
      setBulkRevalidating(false);
    }
  }, [commissionsQueryKey, queryClient, resetPagination]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    if (!window.confirm(`Delete ${n} commission payment${n === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const ops = ids.map(id => ({ op: 'remove' as const, path: `/${id}` }));
      await patchCommissionPayments(ops);
      clearSelection();
      setAllPayments([]);
      queryClient.invalidateQueries({ queryKey: [scope === 'user' && userId ? `commission-payments:${userId}` : 'commission-payments'] });
    } catch (err: any) {
      const data = err?.response?.data;
      window.alert(`Bulk delete failed: ${data?.message || err?.message || err}`);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, clearSelection, setAllPayments, queryClient, scope, userId]);

  // URL sync for filters/sort/bucket — gated on pathname when urlPathname is provided.
  // bucket_by is only persisted when not locked by the parent (locked views
  // don't expose the toggle, so persisting would just be noise).
  useEffect(() => {
    if (!urlPathname || location.pathname !== urlPathname) return;
    const params = new URLSearchParams(searchParams);
    for (const key of filterKeys) {
      if (filters[key]) params.set(key, filters[key]);
      else params.delete(key);
    }
    if (sortField && sortField !== defaultSortField) params.set('sort_field', sortField);
    else params.delete('sort_field');
    if (sortDir && sortDir !== 'desc') params.set('sort_direction', sortDir);
    else params.delete('sort_direction');
    if (!lockBucketBy && bucketBy !== 'booking_date') params.set('bucket_by', bucketBy);
    else params.delete('bucket_by');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortField, sortDir, bucketBy, location.pathname, urlPathname]);

  // Modals.
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [selectedBankTxn, setSelectedBankTxn] = useState<BankTransaction | null>(null);

  const openBankTxnModal = useCallback(async (btID: string) => {
    try {
      const bt = await getBankTransactionsTransactionId(btID);
      setSelectedBankTxn(bt as BankTransaction);
    } catch (err: any) {
      window.alert(`Failed to load bank transaction ${btID}: ${err?.response?.data?.message || err?.message || err}`);
    }
  }, []);

  // Filter definitions — User filter only in admin scope.
  const userFilterOptions = useMemo(() => {
    if (!allUsers) return [];
    return allUsers
      .filter(u => u.id && u.active !== false)
      .map(u => ({ value: u.id!, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || (u.email || u.id!) }));
  }, [allUsers]);

  const filterDefs = useMemo((): FilterDefinition[] => {
    const defs: FilterDefinition[] = [];
    if (scope === 'admin') {
      defs.push({ type: 'select', field: 'user_id', label: 'User', icon: 'bi-person', options: userFilterOptions });
    }
    defs.push({ type: 'multiselect', field: 'source_type', label: 'Source', icon: 'bi-database', options: sourceTypeOptions });
    defs.push({ type: 'text', field: 'search', label: 'Search', icon: 'bi-search', placeholder: 'Search notes and method...' });
    return defs;
  }, [scope, userFilterOptions]);

  // Columns — memoized with empty deps so DataTable's column manager (which
  // snapshots defaultColumns at mount) never sees stale closures. Per-cell
  // state is read via refs updated during render.
  const columns: ColumnConfig<CommissionPaymentRecord>[] = useMemo(() => {
    const cols: ColumnConfig<CommissionPaymentRecord>[] = [];
    if (canDelete) {
      cols.push({
        key: '_select', label: '', width: 44, minWidth: 44,
        headerOnClick: toggleAllOnPage,
        renderHeader: () => {
          const ids = selectedIdsRef.current;
          const payments = allPaymentsRef.current;
          let state: 'none' | 'some' | 'all' = 'none';
          if (ids.size > 0) {
            const allSelected = payments.length > 0 && payments.every(p => p.id ? ids.has(p.id) : false);
            state = allSelected ? 'all' : 'some';
          }
          return <TriStateCheckbox state={state} ariaLabel="Select all on page" />;
        },
        onCellClick: (r: CommissionPaymentRecord) => r.id && toggleRow(r.id),
        renderCell: (r: CommissionPaymentRecord) => {
          const id = r.id || '';
          const checked = selectedIdsRef.current.has(id);
          return (
            <div className="d-flex justify-content-center" style={{ cursor: 'pointer', pointerEvents: 'none' }}>
              <Form.Check
                type="checkbox"
                checked={checked}
                readOnly
                tabIndex={-1}
                aria-label="Select row"
                style={{ margin: 0 }}
              />
            </div>
          );
        },
      });
    }
    cols.push({
      key: 'paid_at', label: 'Paid', width: 130, minWidth: 110, sortable: true,
      renderCell: (r: CommissionPaymentRecord) => formatLocalDate(r.paid_at),
    });
    if (scope === 'admin') {
      cols.push({
        key: 'user_name', label: 'Payee', width: 180, minWidth: 140, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => r.user_name || r.user_id || '',
      });
    }
    cols.push(
      {
        key: 'role', label: 'Role', width: 120, minWidth: 100, sortable: false,
        renderCell: (r: CommissionPaymentRecord) => {
          if (r.role === 'salesperson') {
            return <Badge bg="primary">Salesperson</Badge>;
          }
          if (r.role === 'sales_lead') {
            return <Badge bg="info">Sales Lead</Badge>;
          }
          return null;
        },
      },
      {
        key: 'booking_date', label: 'Booked', width: 130, minWidth: 110, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => formatLocalDate(r.booking_date),
      },
      {
        key: 'amount', label: 'Amount', width: 120, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => <span className="d-block text-end">{formatCurrency(r.amount)}</span>,
      },
      {
        key: 'invoice', label: 'Invoice', width: 180, minWidth: 120, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => {
          if (!r.invoice_id || !r.invoice_src_id) return null;
          const compositeId = `${r.invoice_src_id}-${r.invoice_id}`;
          return (
            <div onClick={(e) => e.stopPropagation()} className="d-flex justify-content-end">
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setInvoiceModalId(compositeId)}
                title={r.customer_name || undefined}
              >
                {r.invoice_display_number || r.invoice_id}
              </Button>
            </div>
          );
        },
      },
      validationErrorsOnly
        ? {
            key: 'validation', label: 'Issue', width: 420, minWidth: 280, sortable: false,
            renderCell: (r: CommissionPaymentRecord) => {
              if (!r.validation_errors) return null;
              // Action set is chosen by issue type, not by severity —
              // every issue is an error visually. Quota-link issues
              // ("no linked sales quota for X (role)…") get the
              // Configure-quota shortcut; memo/attribution mismatches
              // get the Open-Invoice + Override-memo set. The substring
              // match keys off the canonical message produced by
              // RevalidateLinks; if that wording changes, this match
              // must change with it.
              const isQuotaIssue = r.validation_errors.startsWith('no linked sales quota');
              const invoiceCompositeId = r.invoice_src_id && r.invoice_id
                ? `${r.invoice_src_id}-${r.invoice_id}`
                : null;
              const isBank = r.source_type === 'bank_transactions' && !!r.source_label;
              const id = r.id || '';
              const revalidating = id ? revalidatingIdsRef.current.has(id) : false;
              const bulk = bulkRevalidatingRef.current;
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="text-warning mb-1" title={r.validation_errors} style={{ whiteSpace: 'normal' }}>
                    <i className="bi bi-exclamation-triangle-fill me-2" />{r.validation_errors}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {isQuotaIssue ? (
                      r.user_id && (
                        <Button
                          size="sm" variant="outline-primary"
                          onClick={() => navigate(`/commissions/config?config_user=${encodeURIComponent(r.user_id || '')}`)}
                          title={`Open quota configuration for ${r.user_name || r.user_id}`}
                        >
                          <i className="bi bi-sliders me-1" />Configure quota
                          {r.user_name ? ` for ${r.user_name}` : ''}
                        </Button>
                      )
                    ) : (
                      <>
                        {invoiceCompositeId && (
                          <Button
                            size="sm" variant="outline-primary"
                            onClick={() => setInvoiceModalId(invoiceCompositeId)}
                          >
                            <i className="bi bi-file-text me-1" />Open Invoice {r.invoice_display_number || ''}
                          </Button>
                        )}
                        {isBank && (
                          <Button
                            size="sm" variant="outline-primary"
                            onClick={() => r.source_label && openBankTxnModal(r.source_label)}
                          >
                            <i className="bi bi-bank me-1" />Override memo
                          </Button>
                        )}
                      </>
                    )}
                    {id && canRevalidate && (
                      <Button
                        size="sm" variant="outline-success"
                        onClick={() => handleRevalidateRow(id)}
                        disabled={revalidating || bulk}
                        title="Re-run validation against the linked invoice's current state"
                      >
                        <i className={`bi ${revalidating ? 'bi-arrow-repeat' : 'bi-check2-circle'} me-1`} />
                        {revalidating ? 'Re-validating...' : 'Re-validate'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            },
          }
        : {
            key: 'validation', label: '', width: 36, minWidth: 36, sortable: false,
            renderCell: (r: CommissionPaymentRecord) => {
              if (!r.validation_errors) return null;
              return (
                <span
                  className="text-warning"
                  title={r.validation_errors}
                  aria-label={r.validation_errors}
                >
                  <i className="bi bi-exclamation-triangle-fill" />
                </span>
              );
            },
          },
      {
        key: 'payment_method', label: 'Method', width: 130, sortable: true,
      },
      {
        key: 'source_type', label: 'Source', width: 110, minWidth: 90, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => {
          const isBank = r.source_type === 'bank_transactions' && !!r.source_label;
          // Bank rows: badge is clickable to open the txn detail (replaces the
          // old inline bt-id button — bt-id was an opaque ID and the user
          // wants the column to be the source name only).
          if (isBank) {
            return (
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={(e) => { e.stopPropagation(); r.source_label && openBankTxnModal(r.source_label); }}
                title={`Open transaction ${r.source_label}`}
              >
                <Badge bg="secondary">{sourceTypeLabel(r.source_type)}</Badge>
              </Button>
            );
          }
          return (
            <Badge bg="secondary" title={r.source_label || sourceTypeLabel(r.source_type)}>
              {sourceTypeLabel(r.source_type)}
            </Badge>
          );
        },
      },
      {
        key: 'notes', label: 'Notes', width: 260, minWidth: 180, sortable: true,
        renderCell: (r: CommissionPaymentRecord) => {
          const text = r.notes || '';
          return <span className="text-muted" title={text}>{text.length > 80 ? text.substring(0, 80) + '...' : text}</span>;
        },
      },
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationErrorsOnly]);

  const showPicker = !!onPeriodChange;

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <style>{`
        .commission-payments-summary-cards-row {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 1rem !important;
          margin-bottom: 1rem !important;
        }
        .commission-payments-summary-card-col {
          min-width: 200px !important;
          flex: 1 1 200px !important;
        }
      `}</style>

      <div className="commission-payments-summary-cards-row">
        <div className="commission-payments-summary-card-col">
          <Card className="h-100">
            <Card.Body className="text-center p-2">
              <div className="mb-2"><i className="bi bi-cash-coin fs-1 text-success"></i></div>
              <h3 className="mb-1">{formatCurrency(apiSummary?.total_amount)}</h3>
              <p className="text-muted mb-0 small">Total Paid</p>
            </Card.Body>
          </Card>
        </div>
        <div className="commission-payments-summary-card-col">
          <Card className="h-100">
            <Card.Body className="text-center p-2">
              <div className="mb-2"><i className="bi bi-person fs-1 text-primary"></i></div>
              <h3 className="mb-1">{formatCurrency(apiSummary?.salesperson_amount)}</h3>
              <p className="text-muted mb-0 small">Salesperson</p>
            </Card.Body>
          </Card>
        </div>
        <div className="commission-payments-summary-card-col">
          <Card className="h-100">
            <Card.Body className="text-center p-2">
              <div className="mb-2"><i className="bi bi-person-badge fs-1 text-info"></i></div>
              <h3 className="mb-1">{formatCurrency(apiSummary?.sales_lead_amount)}</h3>
              <p className="text-muted mb-0 small">Sales Lead</p>
            </Card.Body>
          </Card>
        </div>
      </div>

      {revalidateBanner && (
        <Alert
          variant={revalidateBanner.variant}
          dismissible
          onClose={() => setRevalidateBanner(null)}
          className="mb-2"
        >
          {revalidateBanner.message}
        </Alert>
      )}

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className={`bi me-1 ${validationErrorsOnly ? 'bi-exclamation-triangle-fill' : 'bi-cash-coin'}`}></i>
                  {validationErrorsOnly ? 'Commission Issues' : showPicker ? 'Payments for' : 'Commission Payments'}
                </span>
                {!validationErrorsOnly && showPicker && (
                  <>
                    <DatePeriodNavigation
                      onPeriodChange={onPeriodChange!}
                      dateFormat="date-only"
                      initialNavigationMode={(period || 'month') as any}
                      initialDateRange={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
                    />
                    <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                  </>
                )}
                {!lockBucketBy && (
                  <>
                    <div className="border-end border-primary mx-1" style={{ height: '24px' }}></div>
                    <div
                      className="btn-group btn-group-sm"
                      role="group"
                      aria-label="Bucket commission payments by booking date or paid date"
                      title="Booking-date: rows are scoped to the period of the invoice booked. Paid-date: rows are scoped to when cash cleared (cash-flow / reconciliation view)."
                    >
                      <Button
                        variant={bucketBy === 'booking_date' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => handleBucketChange('booking_date')}
                      >
                        <i className="bi bi-calendar-event me-1"></i>Booked
                      </Button>
                      <Button
                        variant={bucketBy === 'paid_at' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => handleBucketChange('paid_at')}
                      >
                        <i className="bi bi-cash-stack me-1"></i>Paid
                      </Button>
                    </div>
                  </>
                )}
                <Button
                  variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {validationErrorsOnly && canRevalidate && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleRevalidateAll}
                  disabled={bulkRevalidating || revalidatingIds.size > 0}
                  title="Re-run validation for every flagged commission payment against current invoice state"
                >
                  <i className={`bi ${bulkRevalidating ? 'bi-arrow-repeat' : 'bi-check2-circle'} me-1`} />
                  {bulkRevalidating ? 'Re-validating all...' : 'Re-validate all issues'}
                </Button>
              )}
              {canDelete && hasSelection && (
                <>
                  <div className="bg-header-control rounded px-3 py-1">
                    <span className="text-primary">{selectedIds.size} selected</span>
                  </div>
                  <Button variant="danger" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting}>
                    <i className="bi bi-trash me-1"></i>
                    {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size}`}
                  </Button>
                  <Button variant="outline-light" size="sm" onClick={clearSelection} disabled={bulkDeleting}>
                    Clear
                  </Button>
                </>
              )}
              <div className="bg-header-control rounded px-3 py-1">
                <span className="text-primary">
                  {totalCount !== undefined ? `${totalCount} payments` : 'No payments found'}
                </span>
              </div>
            </div>
          </div>
          {showFilterBar && (
            <FilterBar
              filters={filters}
              filterDefs={filterDefs}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          )}
        </Card.Header>
        <ApiErrorBanner error={paginationError} />
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={allPayments}
            columns={columns}
            getRowKey={getRowKey}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); clearSelection(); setAllPayments([]); }}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            preferencesKey={preferencesKey}
            userId={currentUserId}
            userPreferences={userPreferences}
            maxHeight="100%"
            enableStickyHeader={true}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            emptyState={<Alert variant="secondary" className="m-3">No commission payments found.</Alert>}
          />
        </Card.Body>
      </Card>

      <InvoiceDetailModal
        show={!!invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
        invoiceId={invoiceModalId}
        onInvoiceUpdated={() => {
          queryClient.invalidateQueries({ queryKey: [scope === 'user' && userId ? `commission-payments:${userId}` : 'commission-payments'] });
        }}
      />

      {selectedBankTxn && (
        <TransactionDetailModal
          key={selectedBankTxn.id}
          transaction={selectedBankTxn}
          onClose={() => setSelectedBankTxn(null)}
          onUpdated={() => {
            queryClient.invalidateQueries({ queryKey: [scope === 'user' && userId ? `commission-payments:${userId}` : 'commission-payments'] });
          }}
        />
      )}
    </div>
  );
};

export default CommissionPaymentsTable;
